import { api } from '@xtnd/web/shared/api';
import { ActivityCategory } from '@repo/types';
import { Activity } from '../types/activity';

export interface ActivityLogResponse {
  data: Activity[];
  lastKey?: string;
}

interface GetActivityLogsParams {
  category: ActivityCategory;
  eventId: string;
  limit?: number;
  lastKey?: string;
}

/**
 * Service for fetching activity logs from the backend.
 *
 * API Endpoint: GET /activity-logs/:category/:eventId
 *
 * @example
 * ```typescript
 * const response = await activityLogApi.getActivityLogs({
 *   category: ActivityCategory.INVOICE,
 *   eventId: 'INV-12345',
 *   limit: 20,
 *   lastKey: 'eyJ0ZW5hbnRJZCI6...'
 * });
 * ```
 */
class ActivityLogApiService {
  private readonly baseResource = '/activity-logs';

  /**
   * Retrieves activity logs for a specific event with cursor-based pagination.
   *
   * @param params - Parameters including category, eventId, limit, and lastKey for pagination
   * @returns Activity logs with pagination cursor
   */
  async getActivityLogs(params: GetActivityLogsParams): Promise<ActivityLogResponse> {
    const { category, eventId, limit, lastKey } = params;

    const queryParams = new URLSearchParams();
    if (limit) {
      queryParams.set('limit', String(limit));
    }
    if (lastKey) {
      queryParams.set('lastKey', lastKey);
    }

    const queryString = queryParams.toString();
    const endpoint = `${this.baseResource}/${category}/${eventId}${queryString ? `?${queryString}` : ''}`;

    return await api.get<ActivityLogResponse>(endpoint);
  }
}

export const activityLogApi = new ActivityLogApiService();
